// src/pages/News/ArtificialIntelligence/AIRevolution.js
import React from 'react';
import { Layout, Typography, Card, Row, Col, Button } from 'antd';
import { Link } from 'react-router-dom';
import { FaTwitter, FaFacebookF, FaLinkedinIn, FaWhatsapp, FaEnvelope } from 'react-icons/fa'; // Social media icons

const { Content } = Layout;
const { Title, Paragraph } = Typography;

const sections = [
  {
    title: 'AI in Healthcare',
    description: 'AI-powered diagnostic tools are enhancing disease detection and patient care. Startups like Zipline use AI-driven drones to deliver medical supplies, while AI algorithms assist doctors in diagnosing diseases such as malaria and tuberculosis more efficiently.',
  },
  {
    title: 'AI in Agriculture',
    description: 'AI is helping African farmers increase productivity through smart farming techniques. Machine learning models analyze weather patterns and soil conditions to provide real-time insights, reducing crop losses and optimizing resource use.',
  },
  {
    title: 'AI in Education',
    description: 'With the rise of e-learning platforms, AI-powered tutoring systems are personalizing education for students across the continent. Platforms like JectHub are providing AI-driven learning experiences, bridging the gap between theoretical knowledge and real-world applications.',
  },
  {
    title: 'AI in Financial Services',
    description: 'Fintech companies in Africa are leveraging AI to enhance financial inclusion. AI-powered credit scoring systems help unbanked populations access loans, while fraud detection algorithms ensure secure transactions.',
  },
  {
    title: 'AI in Smart Cities & Governance',
    description: 'Governments are adopting AI to improve urban planning, traffic management, and public safety. AI-driven analytics help optimize resource allocation and streamline decision-making processes.',
  },
];

// Related news posts filtered from NewsPage.js (only AI-related for relevance)
const relatedPosts = [
  { id: 4, title: "Machine Learning in Healthcare", date: "2024-11-20", description: "AI-driven innovations in healthcare and diagnostics...", tags: ["Artificial Intelligence"], image: "https://www.jecthub.com/images/ai-ml.jpeg", views: 1350 },
  { id: 6, title: "AI for Social Good", date: "2025-02-18", description: "How AI is being used for social impact and solving global challenges...", tags: ["Artificial Intelligence"], image: "https://www.jecthub.com/images/ai-ml.jpeg", views: 1300 },
  { id: 7, title: "The Role of AI in Climate Change", date: "2025-02-05", description: "Exploring how artificial intelligence is helping fight climate change...", tags: ["Artificial Intelligence", "Environment"], image: "https://www.jecthub.com/images/ai-ml.jpeg", views: 1050 },
  { id: 13, title: "The Rise of Edge AI", date: "2025-03-02", description: "How edge AI is revolutionizing real-time computing and IoT devices...", tags: ["Artificial Intelligence", "IoT"], image: "https://www.jecthub.com/images/ai-ml.jpeg", views: 1250 },
];

// Share functionality
const pageUrl = window.location.href; // Current page URL
const pageTitle = "AI Revolution in Africa";

const shareLinks = {
  twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`,
  facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`,
  linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(pageUrl)}&title=${encodeURIComponent(pageTitle)}`,
  whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${pageTitle} - ${pageUrl}`)}`,
  email: `mailto:?subject=${encodeURIComponent(pageTitle)}&body=${encodeURIComponent(`Check out this article: ${pageUrl}`)}`,
};

function AIRevolution() {
  return (
    <Layout>
      <Content style={{ padding: '50px', backgroundColor: '#f0f2f5' }}>
        {/* Header Section */}
        <Row justify="center">
          <Col span={24} style={{ textAlign: 'center' }}>
            <Title level={2} style={{ color: '#1890ff', marginBottom: '10px' }}>
              AI Revolution in Africa
            </Title>
            <Paragraph style={{ fontSize: '16px', color: '#666' }}>
              <strong>Published on February 10, 2025</strong>
            </Paragraph>
          </Col>
        </Row>

        {/* Share Buttons */}
        <Row justify="center" style={{ marginBottom: '20px' }}>
          <Col>
            <Button
              type="link"
              href={shareLinks.twitter}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#1DA1F2', padding: '0 10px' }}
            >
              <FaTwitter size={20} />
            </Button>
            <Button
              type="link"
              href={shareLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#3B5998', padding: '0 10px' }}
            >
              <FaFacebookF size={20} />
            </Button>
            <Button
              type="link"
              href={shareLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#0077B5', padding: '0 10px' }}
            >
              <FaLinkedinIn size={20} />
            </Button>
            <Button
              type="link"
              href={shareLinks.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#25D366', padding: '0 10px' }}
            >
              <FaWhatsapp size={20} />
            </Button>
            <Button
              type="link"
              href={shareLinks.email}
              style={{ color: '#D44638', padding: '0 10px' }}
            >
              <FaEnvelope size={20} />
            </Button>
          </Col>
        </Row>

        {/* Introduction */}
        <Row justify="center">
          <Col span={20}>
            <Paragraph style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '30px' }}>
              Artificial Intelligence (AI) is rapidly reshaping industries across Africa, driving innovation, boosting economic growth, and solving critical challenges. As the continent embraces the digital revolution, AI is playing a pivotal role in transforming sectors such as healthcare, agriculture, education, finance, and governance.
            </Paragraph>
          </Col>
        </Row>

        {/* Main Sections */}
        <Row gutter={[16, 16]} justify="center">
          {sections.map((section, index) => (
            <Col xs={24} sm={12} md={12} lg={8} key={index}>
              <Card
                title={<Title level={4} style={{ color: '#333' }}>{section.title}</Title>}
                bordered={false}
                style={{ borderRadius: '8px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
              >
                <Paragraph style={{ color: '#666', fontSize: '14px' }}>
                  {section.description}
                </Paragraph>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Future Outlook */}
        <Row justify="center" style={{ marginTop: '40px' }}>
          <Col span={20}>
            <Title level={3} style={{ color: '#1890ff' }}>
              The Future of AI in Africa
            </Title>
            <Paragraph style={{ fontSize: '16px', lineHeight: '1.6' }}>
              As AI adoption accelerates, collaboration between governments, startups, and educational institutions will be crucial. Investment in AI talent and infrastructure will determine how Africa leverages AI to drive sustainable development and global competitiveness.
            </Paragraph>
            <Paragraph style={{ fontSize: '16px', lineHeight: '1.6' }}>
              JectHub remains committed to fostering AI education and innovation, ensuring that Africa is at the forefront of the AI revolution.
            </Paragraph>
            <Paragraph style={{ fontSize: '14px', fontStyle: 'italic', color: '#888' }}>
              Stay updated with JectHub for more insights on AI and emerging technologies!
            </Paragraph>
          </Col>
        </Row>

        {/* Related News Section */}
        <Row justify="center" style={{ marginTop: '40px' }}>
          <Col span={24}>
            <Title level={3} style={{ color: '#1890ff', marginBottom: '20px' }}>
              Related News
            </Title>
            <Row gutter={[16, 16]}>
              {relatedPosts.map((post) => (
                <Col xs={24} sm={12} md={6} key={post.id}>
                  <Card
                    hoverable
                    cover={<img alt={post.title} src={post.image} style={{ height: '120px', objectFit: 'cover' }} />}
                    style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
                  >
                    <Title level={5} style={{ marginBottom: '5px', color: '#333' }}>
                      {post.title}
                    </Title>
                    <Paragraph type="secondary" style={{ fontSize: '12px' }}>
                      {post.date}
                    </Paragraph>
                    <Paragraph style={{ color: '#666', fontSize: '13px' }}>
                      {post.description}{' '}
                      <Link to="/newsroom">Read more</Link>
                    </Paragraph>
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
}

export default AIRevolution;